import AppRoutes from "@/constants/routes/AppRoutes";
import { DataSource } from "@/config/datasources";
import { InboundChange } from "@/models/legislation/metadata/ConsolidationMetadata";
import { TimelineItem } from "@/models/legislation/Timeline";
import {
  flattenConsolidationTimeline,
  flattenTimelineItems,
} from "@/lib/consolidationViewerUtils";
import {
  DOCUMENT_TYPE_ID_LOV,
  isConsolidationAnchorType,
  isIkrafttraedelseBekendtgoerelseDocumentType,
} from "@/utils/legislationDocumentTypeUtils";

/** Query param preserving the originating Retsinformation timeline when viewing ændringslove. */
export const TIMELINE_ROOT_PARAM = "timeline_root";

/** Query param selecting which timeline strip to show within a timeline_root universe. */
export const TIMELINE_SCOPE_PARAM = "timeline_scope";

/** Query param identifying which flat-timeline instance is active (for duplicate accessions). */
export const TIMELINE_AT_PARAM = "timeline_at";

export type TimelineScope = "parent" | "own";

const CHANGE_LAW_DOCUMENT_TYPE_IDS = new Set(["20", "80"]);

/** True for dedicated ændringslov document types (20, 80). */
export const isChangelawDocumentType = (
  documentTypeId: string | number | undefined | null
): boolean => CHANGE_LAW_DOCUMENT_TYPE_IDS.has(String(documentTypeId ?? ""));

/**
 * True when a node on a flattened timeline is an ændringslov:
 * documentTypeId 20/80, ikræfttrædelses-bekendtgørelse (90), or Lov (10)
 * when it is not the first timeline entry.
 */
export const isTimelineAmendmentAtPosition = (
  documentTypeId: string | number | undefined | null,
  flatTimelineIndex: number
): boolean => {
  if (isChangelawDocumentType(documentTypeId)) return true;
  if (isIkrafttraedelseBekendtgoerelseDocumentType(documentTypeId)) return true;
  return (
    Number(documentTypeId) === DOCUMENT_TYPE_ID_LOV && flatTimelineIndex > 0
  );
};

/**
 * True for consolidation progress counting: ændringslove (20/80) and Lov (10)
 * after index 0. Excludes ikræfttrædelses-bekendtgørelse (90), which sets
 * activation dates but is not a konsolidering amendment.
 */
export const isConsolidationAmendmentAtPosition = (
  documentTypeId: string | number | undefined | null,
  flatTimelineIndex: number
): boolean => {
  if (isChangelawDocumentType(documentTypeId)) return true;
  return (
    Number(documentTypeId) === DOCUMENT_TYPE_ID_LOV && flatTimelineIndex > 0
  );
};

const countAmendmentsAfterAccessionUntilNextLbk = (
  flat: TimelineItem[],
  accessionNumber: string,
  isAmendmentAtPosition: (
    documentTypeId: string | number | undefined | null,
    flatTimelineIndex: number
  ) => boolean
): number => {
  const index = flat.findIndex(
    (item) => item.accessionNumber === accessionNumber
  );
  if (index < 0) return 0;

  let count = 0;
  for (let i = index + 1; i < flat.length; i++) {
    if (isConsolidationAnchorType(flat[i].documentTypeId)) {
      break;
    }
    if (isAmendmentAtPosition(flat[i].documentTypeId, i)) {
      count++;
    }
  }
  return count;
};

/**
 * Amendments on the timeline strictly after the anchor, stopping at the next
 * consolidation anchor (LBK 30 or BEK 60). Counts ændringslove/ændringsbekendtgørelser
 * (20/80) and Lov (10) when not at timeline index 0.
 */
export const countTimelineAmendmentsAfter = (
  timelineItems: TimelineItem[] | undefined,
  accessionNumber: string | undefined
): number => {
  if (!timelineItems?.length || !accessionNumber?.trim()) return 0;
  return countAmendmentsAfterAccessionUntilNextLbk(
    flattenTimelineItems(timelineItems),
    accessionNumber,
    isTimelineAmendmentAtPosition
  );
};

/**
 * Consolidation amendments after the anchor (excludes type 90).
 * Aligns with API TimelineAmendmentCountAfter and progress denominators.
 */
export const countConsolidationAmendmentsAfter = (
  timelineItems: TimelineItem[] | undefined,
  accessionNumber: string | undefined,
  documentTypeId?: string | number | null
): number => {
  if (!timelineItems?.length || !accessionNumber?.trim()) return 0;
  // BEK families skip nested Lov refs so the denominator matches the API's
  // FlattenBekConsolidationLineage-based count used by list/search.
  return countAmendmentsAfterAccessionUntilNextLbk(
    flattenConsolidationTimeline(timelineItems, documentTypeId),
    accessionNumber,
    isConsolidationAmendmentAtPosition
  );
};

/** @deprecated Use countTimelineAmendmentsAfter — same rules since consolidation scope alignment. */
export const countChangelawAmendmentsAfter = countTimelineAmendmentsAfter;

export const resolveAnchorAccessionOnTimeline = (
  timelineItems: TimelineItem[] | undefined,
  anchorAccession: string | undefined,
  documentId?: string
): string | undefined => {
  if (!timelineItems?.length) return anchorAccession ?? documentId;

  const flat = flattenTimelineItems(timelineItems);
  for (const candidate of [anchorAccession, documentId]) {
    const trimmed = candidate?.trim();
    if (trimmed && flat.some((item) => item.accessionNumber === trimmed)) {
      return trimmed;
    }
  }

  return anchorAccession ?? documentId;
};

export type ScopeInboundChangesOptions = {
  /** When true, excludes type-90 ikræfttrædelses-bekendtgørelser (consolidation progress only). */
  consolidationScope?: boolean;
  /** Row document type — lets BEK/Ændringsbekendtgørelse rows use the BEK lineage flatten. */
  documentTypeId?: string | number | null;
};

/** Inbound changes whose changelaw appears as an amendment after the anchor until the next consolidation anchor (LBK 30 or BEK 60). */
export const scopeInboundChangesAfterAnchorOnTimeline = (
  timelineItems: TimelineItem[] | undefined,
  anchorAccession: string | undefined,
  inboundChanges: ReadonlyArray<InboundChange> | undefined,
  options?: ScopeInboundChangesOptions
): InboundChange[] => {
  if (!timelineItems?.length || !anchorAccession?.trim() || !inboundChanges?.length) {
    return [];
  }

  const isAmendmentAtPosition = options?.consolidationScope
    ? isConsolidationAmendmentAtPosition
    : isTimelineAmendmentAtPosition;

  // Consolidation scoping mirrors the API window: BEK families skip nested Lov
  // refs so the scoped numerator lines up with the BEK-aware denominator.
  const flat = options?.consolidationScope
    ? flattenConsolidationTimeline(timelineItems, options.documentTypeId)
    : flattenTimelineItems(timelineItems);
  const anchorIndex = flat.findIndex(
    (item) => item.accessionNumber === anchorAccession
  );
  if (anchorIndex < 0) return [];

  const afterAnchorAccessions = new Set<string>();
  for (let i = anchorIndex + 1; i < flat.length; i++) {
    if (isConsolidationAnchorType(flat[i].documentTypeId)) {
      break;
    }
    const accession = flat[i].accessionNumber;
    if (!accession) continue;
    if (isAmendmentAtPosition(flat[i].documentTypeId, i)) {
      afterAnchorAccessions.add(accession);
    }
  }

  return inboundChanges.filter((entry) =>
    afterAnchorAccessions.has(entry.changelawAccession)
  );
};

const isOnParentTimelineUrl = (
  timelineRoot?: string | null,
  documentId?: string | null
): boolean => {
  const root = timelineRoot?.trim();
  const doc = documentId?.trim();
  return Boolean(root && doc && doc !== root);
};

/**
 * True when legislation is viewed within a parent timeline universe (timeline_root).
 * Includes ændringslove, Lov amendments, and ikræfttrædelses-bekendtgørelser on the strip.
 */
export const isTimelineUniverseChildLegislation = (
  documentTypeId: string | number | undefined | null,
  options?: {
    timelineRoot?: string | null;
    documentId?: string | null;
    flatTimelineIndex?: number;
  }
): boolean => {
  if (isChangelawDocumentType(documentTypeId)) return true;

  if (options?.flatTimelineIndex != null) {
    return isTimelineAmendmentAtPosition(
      documentTypeId,
      options.flatTimelineIndex
    );
  }

  if (
    Number(documentTypeId) === DOCUMENT_TYPE_ID_LOV ||
    isIkrafttraedelseBekendtgoerelseDocumentType(documentTypeId)
  ) {
    return isOnParentTimelineUrl(options?.timelineRoot, options?.documentId);
  }

  return false;
};

/**
 * True when legislation acts as an ændringslov in context.
 * With timeline position: Lov (10) after index 0 counts as amendment.
 * Without timeline: Lov (10) on a parent timeline (documentId ≠ timeline_root) counts as amendment.
 *
 * @deprecated Prefer isTimelineUniverseChildLegislation for parent-timeline detection.
 */
export const isTimelineAmendmentLegislation = (
  documentTypeId: string | number | undefined | null,
  options?: {
    timelineRoot?: string | null;
    documentId?: string | null;
    flatTimelineIndex?: number;
  }
): boolean => {
  if (isChangelawDocumentType(documentTypeId)) return true;

  if (options?.flatTimelineIndex != null) {
    return isTimelineAmendmentAtPosition(
      documentTypeId,
      options.flatTimelineIndex
    );
  }

  if (
    Number(documentTypeId) === DOCUMENT_TYPE_ID_LOV ||
    isIkrafttraedelseBekendtgoerelseDocumentType(documentTypeId)
  ) {
    return isOnParentTimelineUrl(options?.timelineRoot, options?.documentId);
  }

  return false;
};

export const parseTimelineRootParam = (
  searchParams: URLSearchParams
): string | null => searchParams.get(TIMELINE_ROOT_PARAM)?.trim() || null;

export const parseTimelineScopeParam = (
  searchParams: URLSearchParams
): TimelineScope => {
  const value = searchParams.get(TIMELINE_SCOPE_PARAM)?.trim().toLowerCase();
  if (value === "own") return "own";
  return "parent";
};

export const parseTimelineAtParam = (
  searchParams: URLSearchParams
): number | null => {
  const raw = searchParams.get(TIMELINE_AT_PARAM);
  if (raw == null || raw.trim() === "") return null;
  const index = Number(raw);
  if (!Number.isInteger(index) || index < 0) return null;
  return index;
};

export const appendTimelineAtParam = (
  path: string,
  flatTimelineIndex: number
): string => {
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${TIMELINE_AT_PARAM}=${flatTimelineIndex}`;
};

export const isShowingParentTimelineScope = (scope: TimelineScope): boolean =>
  scope === "parent";

/** True when timeline_root is set and the route document is not the root. */
export const isInTimelineUniverse = (
  timelineRoot: string | null,
  documentId?: string | null
): boolean => isOnParentTimelineUrl(timelineRoot, documentId);

export const resolveShowingParentTimeline = (
  timelineRoot: string | null,
  documentId: string | undefined | null,
  scope: TimelineScope
): boolean => {
  if (!isInTimelineUniverse(timelineRoot, documentId)) return false;
  return isShowingParentTimelineScope(scope);
};

/**
 * Identity of the law family a timeline belongs to: the original Hovedlov
 * (isMainLaw) accession, falling back to the first flattened node. Every LBK in
 * a family shares the same full timeline_gold, so this is stable across windows.
 * Mirrors the backend FamilyIdExtractor.
 */
export const resolveTimelineFamilyRoot = (
  items: TimelineItem[] | undefined
): string | null => {
  const flat = flattenTimelineItems(items);
  if (!flat.length) return null;
  const mainLaw = flat.find((item) => item.isMainLaw);
  return (mainLaw ?? flat[0]).accessionNumber ?? null;
};

/**
 * True when the current document's own timeline belongs to a different law
 * family than the parent timeline. Navigating between LBK windows of the same
 * law shares one family root, so it does not count as a context switch.
 */
export const documentHasDistinctOwnTimeline = (
  documentId: string | undefined,
  ownTimelineItems: TimelineItem[] | undefined,
  parentTimelineItems?: TimelineItem[] | undefined
): boolean => {
  if (!documentId?.trim() || !ownTimelineItems?.length) return false;

  const ownFamilyRoot = resolveTimelineFamilyRoot(ownTimelineItems);
  const parentFamilyRoot = resolveTimelineFamilyRoot(parentTimelineItems);
  if (!ownFamilyRoot || !parentFamilyRoot) return false;

  return ownFamilyRoot !== parentFamilyRoot;
};

export const appendTimelineRootParam = (
  path: string,
  timelineRoot: string
): string => {
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${TIMELINE_ROOT_PARAM}=${encodeURIComponent(timelineRoot)}`;
};

export const appendTimelineScopeParam = (
  path: string,
  scope: TimelineScope
): string => {
  if (scope === "parent") return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${TIMELINE_SCOPE_PARAM}=${encodeURIComponent(scope)}`;
};

export const resolveTimelineFetchExternalId = (
  documentId: string | undefined,
  timelineRoot: string | null,
  showingParentTimeline: boolean
): string => {
  if (showingParentTimeline && timelineRoot) return timelineRoot;
  return documentId ?? "";
};

export const buildLegislationHtmlPath = (
  documentId: string,
  datasource: string
): string => AppRoutes.legislationHTML(documentId, datasource);

export const buildTimelineUniverseNavigationPath = (
  targetAccessionNumber: string,
  datasource: string,
  timelineRootAccession: string | undefined,
  flatTimelineIndex?: number
): string => {
  const base = buildLegislationHtmlPath(targetAccessionNumber, datasource);
  let path = base;
  if (
    timelineRootAccession &&
    timelineRootAccession !== targetAccessionNumber
  ) {
    path = appendTimelineRootParam(path, timelineRootAccession);
  }
  if (flatTimelineIndex != null) {
    path = appendTimelineAtParam(path, flatTimelineIndex);
  }
  return path;
};

export const buildChangelawNavigationPath = (
  changelawAccession: string,
  timelineRootAccession: string
): string =>
  appendTimelineRootParam(
    AppRoutes.legislationHTML(changelawAccession, DataSource.Retsinformation),
    timelineRootAccession
  );
